import { Injectable } from '@angular/core';
import {
  interval,
  Observable,
  of,
  startWith,
  switchMap,
  takeWhile,
  timer,
} from 'rxjs';
import { LiveChatServiceInterface } from './live-chat-service.interface';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { v4 as uuidv4 } from 'uuid';
import { LiveChatMethods } from '../../types/enum';

@Injectable({
  providedIn: 'root',
})
export class GenesysCxWebMessagingService implements LiveChatServiceInterface {
  private deploymentId = '2cbc5963-f789-4708-987d-f212ad82e98c'; // WebMessaging_POC
  private chatKey = '';
  private webSocketSubject: WebSocketSubject<any> | undefined;
  liveChatResponse$: Observable<LiveChatResponse>;

  createLiveChat(): Observable<any> {
    this.chatKey = uuidv4();
    return of({
      ChatID: '',
      ChatKey: this.chatKey,
      WebSocketURL: `wss://webmessaging.cac1.pure.cloud/v1?deploymentId=${this.deploymentId}`,
      VisitorID: '',
    });
  }

  createLiveChatWebSocket({ webSocketURL = '', chatKey = '' }): void {
    this.chatKey = chatKey;
    this.heartbeat();
    this.createWebSocket(webSocketURL);
    this.startGuestSession();
  }

  /* sends customer message to operator */
  sendMessage({
    message = '',
    firstName = '',
    lastName = '',
    sessionId = '',
    isFirstConnectionMessage = false,
  }): void {
    let customAttributes = {};
    if (isFirstConnectionMessage) {
      customAttributes = {
        'first-name': firstName,
        'last-name': lastName,
        'phone-number': '',
        email: 'noha.mohamed@rci.rogers.ca',
        product: 'credit-support',
        intent: 'credit-support',
        brand: 'Fido',
        language: 'English',
        'transcript-url': 'https://www.rogers.com',
        'safesend-base-url': 'https://qa01-safesendservice.rogers.com/safesend',
        'digital-chat-id': sessionId,
        OriginationPageCategory: window.parent.location.pathname,
      };
    }

    this.webSocketSubject?.next({
      action: 'onMessage',
      token: this.chatKey,
      message: {
        type: 'Text', // TODO: Expand message types in future.
        text: message,
        channel: {
          metadata: {
            customAttributes: customAttributes,
          },
        },
      },
    });
  }

  /* sends customer typing indicator to operator */
  setUserTyping(): void {
    this.webSocketSubject?.next({
      action: 'onMessage',
      token: this.chatKey,
      message: {
        type: 'Event',
        events: [
          {
            eventType: 'Typing',
            typing: {
              type: 'On',
            },
          },
        ],
      },
    });
  }

  /* closes the connection */
  closeConnection({ endChatMessage = '' }) {
    if (this.chatKey) {
      this.sendMessage({ message: endChatMessage });
      this.webSocketSubject?.next({
        action: 'closeSession',
        token: this.chatKey,
        closeAllConnections: true,
      });
      this.chatKey = '';
    }
  }

  private heartbeat() {
    interval(30000) // every 30 seconds
      .pipe(takeWhile(() => !!this.chatKey))
      .subscribe(() => {
        this.webSocketSubject?.next({
          action: 'echo',
          message: { type: 'Text', text: '' },
        });
      });
  }

  private startGuestSession() {
    this.webSocketSubject?.next({
      action: 'configureSession',
      deploymentId: this.deploymentId,
      token: this.chatKey,
    });
  }

  private createWebSocket(websocketUrl: string): void {
    this.webSocketSubject = webSocket(websocketUrl);
    this.liveChatResponse$ = this.webSocketSubject?.pipe(
      switchMap((event: any) => this.listenToWebSocketMessages(event))
    );
  }

  /* listens to websocket messages */
  private listenToWebSocketMessages(event: any): Observable<LiveChatResponse> {
    if (event?.body?.connected) {
      return of(this.onConnectionSuccess());
    }

    if (event.body.direction === 'Outbound') {
      if (event.class === 'StructuredMessage' && event.body.type === 'Text') {
        if (event.body.originatingEntity === 'Bot') {
          // System message
          return of(
            this.onReceiveSystemMessage(event.body.id, event.body.text)
          );
        }
        if (event.body.originatingEntity === 'Human') {
          // Agent message
          return of(
            this.onReceiveOperatorMessage(
              event.body.id,
              event.body.text,
              event.body.channel?.from?.nickname,
              ''
            )
          );
        }
      }
      if (event.body.type === 'Event') {
        if (event.body.events[0].eventType === 'Typing') {
          if (event.body.events[0].typing.type === 'On') {
            return this.handleOperatorTyping(
              event.body.events[0].typing.duration
            );
          }
        }
        if (event.body.events[0].eventType === 'Presence') {
          if (event.body.events[0].presence?.type === 'Disconnect') {
            return of(this.onAgentCloseConnection());
          }
        }
      }
    }
    return of(null);
  }

  private onConnectionSuccess(): LiveChatResponse {
    return {
      method: LiveChatMethods.setConnection,
    };
  }

  private onReceiveSystemMessage(
    messageId: string,
    message: string
  ): LiveChatResponse {
    if (message.startsWith('$genesys_workflow_response:')) {
      const msg = JSON.parse(message.split('$genesys_workflow_response:')[1]);
      if (msg.conversation_id) {
        return {
          method: LiveChatMethods.setChatId,
          value: msg.conversation_id,
        };
      }
      return;
    }
    return {
      method: LiveChatMethods.receiveSystemMessage,
      value: {
        messageId,
        text: message,
      },
    };
  }

  private onReceiveOperatorMessage(
    messageId: string,
    messageText: string,
    operatorName: string,
    operatorId: string
  ): LiveChatResponse {
    return {
      method: LiveChatMethods.receiveOperatorMessage,
      value: {
        messageId,
        text: messageText,
        operatorName,
        operatorId,
      },
    };
  }

  private handleOperatorTyping(duration: number): Observable<LiveChatResponse> {
    return timer(duration ? duration : 4000).pipe(
      switchMap(() => of(this.setOperatorTyping(false))),
      startWith(this.setOperatorTyping(true))
    );
  }

  private setOperatorTyping(value: boolean): LiveChatResponse {
    return {
      method: LiveChatMethods.setOperatorTyping,
      value,
    };
  }

  private onAgentCloseConnection(): LiveChatResponse {
    return {
      method: LiveChatMethods.closeConnection,
    };
  }
}
